/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ApolloError, gql, useQuery } from '@apollo/client';
import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';

import {
  GetQuotePageRedirectQuery,
  GetQuotePageRedirectQueryVariables,
  useGetQuotePageRedirectQuery
} from '../../generated/graphql';
import { GetQuoteExpired } from '../../generated/rating-quoting-graphql';
import getEnv from '../../utils/getEnv';
import {
  getVersionedCompleteUrl,
  getVersionedPaymentUrl,
  getVersionedTermsUrl
} from '../../utils/getVersionedUrlFromPaymentFrontend';
import { isUnknownFieldError } from '../../utils/graphqlFieldSkew';
import { safeAssignLocation } from '../../utils/url';

interface UseQuotePageRedirectReturn {
  loading: boolean;
  error: ApolloError | undefined;
  data: GetQuotePageRedirectQuery | undefined;
}

/**
 * `getQuotePageRedirect` exactly as it was before the group-wide paid flags
 * were added to `QuotePageRedirectView`.
 *
 * A backend that predates `isGroupFullyPaid` / `isGroupPartiallyPaid` rejects
 * the current document outright: an unknown field is a *validation* error, so
 * the whole operation is refused and no redirect data comes back at all — the
 * quote page then renders its error bubble instead of the quote, for every
 * customer, not just the partially paid groups the flags exist for. That is
 * not hypothetical: this frontend and foxcom-forms-backend deploy separately,
 * so any environment where the backend has not caught up (the development
 * stage the Playwright e2e-coverage run drives, among others) answers exactly
 * this way.
 *
 * Falling back to the pre-flag document keeps the page working and only costs
 * the group-aware routing, which such a backend could not have answered
 * anyway: the flags come back undefined and the redirect reverts to the
 * per-application `policyData` it used before. The fallback costs one refused
 * round trip per quote load, and only while a backend without the fields is
 * answering — once it has them the first attempt succeeds and this document is
 * never sent.
 */
export const GetQuotePageRedirectWithoutGroupPaidDocument = gql`
  query getQuotePageRedirectWithoutGroupPaid($applicationId: ObjectID!) {
    getQuotePageRedirect(applicationId: $applicationId) {
      ownerData {
        applicationId
        authenticatedEmail
        authenticatedAgentEmail
        isTnCLinkSent
        isDocuSignEnvelopeSigned
      }
      applicationData {
        policyFoxdenId
        policyObjectId
        transactionType
      }
      policyData {
        kind
        invoice {
          _id
        }
      }
      quote {
        _id
      }
      invoice {
        _id
      }
    }
  }
`;

/** The fields the fallback document above exists to drop. */
export const GROUP_PAID_FIELDS = ['isGroupFullyPaid', 'isGroupPartiallyPaid'];

/**
 * Hook to fetch redirect data for the quote page
 *
 * Retries without the group-wide paid flags when the backend does not know
 * them yet (see GetQuotePageRedirectWithoutGroupPaidDocument). Every other
 * failure is reported as before.
 */
export function useQuotePageRedirectQuery(
  applicationId: string
): UseQuotePageRedirectReturn {
  const { loading, error, data } = useGetQuotePageRedirectQuery({
    variables: { applicationId }
  });

  const backendPredatesGroupPaidFields = isUnknownFieldError(
    error,
    GROUP_PAID_FIELDS
  );

  // Never issued unless the primary document was actually refused for these
  // fields, so a current backend costs nothing: the query stays skipped and no
  // second request is made.
  const fallback = useQuery<
    GetQuotePageRedirectQuery,
    GetQuotePageRedirectQueryVariables
  >(GetQuotePageRedirectWithoutGroupPaidDocument, {
    variables: { applicationId },
    skip: !backendPredatesGroupPaidFields
  });

  // Reporting the refusal itself would flash the page's error bubble in the
  // render between the two attempts; the retry is in flight, so this is
  // loading, not failure.
  if (backendPredatesGroupPaidFields) {
    return {
      loading: fallback.loading,
      error: fallback.error,
      data: fallback.data
    };
  }

  return { loading, error, data };
}

/**
 * The purchase flags the redirect matrix and the MET-468 lapsed-effective-date
 * decision (expiredQuoteScreen.ts) both read.
 */
export interface QuotePagePurchaseState {
  transactionType: string | null | undefined;
  isTnCSigned: boolean;
  isTnCSent: boolean;
  /** Group-wide fully paid, or this application has a Policy. */
  isPaid: boolean;
  isGroupPartiallyPaid: boolean;
  /**
   * False when the answer came from the pre-flag fallback document above, so
   * the group-wide paid flags are absent and only `policyData` is known.
   */
  hasGroupPaidFlags: boolean;
}

export function getQuotePagePurchaseState(
  redirectData: GetQuotePageRedirectQuery | undefined
): QuotePagePurchaseState | undefined {
  const view = redirectData?.getQuotePageRedirect;
  if (!view) {
    return undefined;
  }
  const { ownerData, policyData, applicationData } = view;
  return {
    transactionType: applicationData?.transactionType,
    isTnCSigned: ownerData?.isDocuSignEnvelopeSigned ?? false,
    isTnCSent: ownerData?.isTnCLinkSent ?? false,
    // Absent (not false) when the answer came from the pre-flag fallback
    // document, so both default to the legacy per-application behavior.
    isPaid: (view.isGroupFullyPaid ?? false) || !!policyData,
    isGroupPartiallyPaid: view.isGroupPartiallyPaid ?? false,
    hasGroupPaidFlags:
      typeof view.isGroupFullyPaid === 'boolean' &&
      typeof view.isGroupPartiallyPaid === 'boolean'
  };
}

/**
 * Determines if a redirect should occur based on quote page redirect data
 * and performs the redirect if necessary
 */
/* Move from quote page, TODO: clean up logic if needed */
export async function handleQuotePageRedirect(
  redirectData: GetQuotePageRedirectQuery | undefined,
  quoteId: string | undefined,
  isAuthenticated: boolean,
  returnToQuoteExpired: GetQuoteExpired | undefined,
  applicationId: string,
  applicationGroupId: string,
  versionData: WorkflowVersion
): Promise<boolean> {
  if (!redirectData?.getQuotePageRedirect) {
    return false;
  }

  const { REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL } = getEnv();
  const vcc = new VersionControllerClient(
    REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
  );

  const { invoice } = redirectData.getQuotePageRedirect;
  const purchaseState = getQuotePagePurchaseState(redirectData)!;
  const { transactionType } = purchaseState;

  // The terms URL is keyed on applicationGroupId and uses the already-resolved
  // versionData, so no per-quote version lookup is needed.
  const proceedToTnCPage = () => {
    safeAssignLocation(getVersionedTermsUrl(applicationGroupId, versionData));
  };

  // The complete URL is keyed on applicationGroupId (like the terms URL); the
  // payment backend resolves it the same way it resolves an invoice id.
  const proceedToPaymentCompletePage = async () => {
    const completeUrl = await getVersionedCompleteUrl(
      applicationId,
      applicationGroupId,
      vcc
    );
    safeAssignLocation(completeUrl);
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if (transactionType! === 'New Business') {
    const {
      isTnCSigned,
      isTnCSent: isTncSent,
      isGroupPartiallyPaid
    } = purchaseState;
    // Partially paid group (one LOB has a Policy, a sibling does not): the
    // quote may no longer be modified and payment is unfinished, so neither
    // the quote screen nor the complete page is right — go straight to the
    // payment page. Checked before the signed/paid matrix because that matrix
    // only sees THIS application's policy and would otherwise route a paid
    // entry application to the complete page (sibling left unpayable) or an
    // unpaid one back to the quote screen.
    if (isGroupPartiallyPaid) {
      safeAssignLocation(
        getVersionedPaymentUrl(applicationGroupId, versionData)
      );
      return true;
    }

    // Group-wide: every quoted LOB has a Policy. The per-application
    // `!!policyData` kept as a fallback for legacy single-LOB data.
    const { isPaid } = purchaseState;

    if (isTnCSigned && isPaid) {
      // redirect to thank you page if signed & paid
      await proceedToPaymentCompletePage();
      return true;
    } else if (isTnCSigned && !isPaid) {
      if (returnToQuoteExpired) {
        return false; // quote itself has expired — show the expire screen
      } else if (quoteId) {
        proceedToTnCPage();
        return true;
      } else {
        return false; // wait for quoteId from Quote component
      }
    } else if (!isTnCSigned && isPaid) {
      if (returnToQuoteExpired && returnToQuoteExpired.isGracePeriodExpired) {
        return false; // if grace period exceeds, show quote expire screen
      } else {
        if (isAuthenticated) {
          // redirect to thank you page if unsigned & paid & brokers
          await proceedToPaymentCompletePage();
          return true;
        } else {
          // redirect to T&C page if unsigned & paid & clients
          if (quoteId) {
            proceedToTnCPage();
            return true;
          } else if (returnToQuoteExpired) {
            proceedToTnCPage();
            return true;
          } else {
            return false; // wait for quoteId
          }
        }
      }
    } else if (!isTnCSigned && !isPaid && isTncSent) {
      if (!returnToQuoteExpired) {
        if (!isAuthenticated) {
          // redirect to T&C page if unsigned & unpaid & clients
          if (quoteId) {
            proceedToTnCPage();
            return true;
          } else {
            return false; // wait for quoteId
          }
        }
        // If authenticated (broker), don't redirect - let Quote component handle it
        return false;
      } else {
        return false; // if the quote is expired, show quote expire screen
      }
    }
  } else if (transactionType! === 'Renewal') {
    if (invoice) {
      proceedToTnCPage();
      return true;
    }
  }

  return false;
}

/**
 * Determines if user should be redirected to Client & Agreement page
 */
export function shouldNavigateToClientAgreement(
  redirectData: GetQuotePageRedirectQuery | undefined,
  isAuthenticated: boolean,
  returnToQuoteExpired: GetQuoteExpired | undefined
): boolean {
  if (!redirectData?.getQuotePageRedirect) {
    return false;
  }

  const {
    ownerData,
    policyData,
    applicationData: { transactionType }
  } = redirectData.getQuotePageRedirect;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if (transactionType! === 'New Business') {
    const isTnCSigned = ownerData?.isDocuSignEnvelopeSigned ?? false;
    const isTncSent = ownerData?.isTnCLinkSent ?? false;
    const isPaid = !!policyData;

    if (
      !isTnCSigned &&
      !isPaid &&
      isTncSent &&
      !returnToQuoteExpired &&
      isAuthenticated
    ) {
      return true;
    }
  }

  return false;
}
